import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  MORSE_CODE,
  MORSE_LESSONS,
  MASTERY_BOX,
  WORD_HITS_NEEDED,
  currentLesson,
  introduce,
  lessonState,
  nextStep,
  recordAnswer,
  unlockedLetters,
  wordMistakes,
  type LessonState,
  type LetterProgress,
  type MorseStep,
  type ProgressMap,
} from '@familyhub/shared';
import { useSession } from '../session';
import { Sheet } from '../platform/Sheet';
import { useHoldUpdates } from '../platform/update';
import { canSeeModule } from '../modules';
import { progressOf, saveLetter, saveLetters, useProgress } from './actions';
import { play, unlockAudio, type Playback } from './player';
import {
  RECOMMENDED,
  canVibrate,
  recommendedMode,
  saveMorseSettings,
  useMorseSettings,
  type MorseSettings,
} from './settings';
import { MORSE_WORDS } from './words';

/**
 * The Morse module — Jurren's own morse course (docs/MORSE.md).
 *
 * Two modes on one screen. The **overview** is the ladder of lessons and the
 * letters learned so far, each with the learner's own ezelsbruggetje. **Oefenen**
 * is the lesson loop: the shared planner (`nextStep`) decides each step — show a
 * new letter, ask a letter by ear, or play a word to type — and every answer is
 * an optimistic local write replayed through the outbox, like every module.
 *
 * Sound, lamp and vibration are played from one timeline (`player.ts`).
 */

/** `.-` drawn as `· −`. Stored spelling stays `.`/`-`; this is presentation. */
export function glyphs(code: string): string {
  return [...code].map((c) => (c === '.' ? '·' : '−')).join(' ');
}

const codeOf = (letter: string) => MORSE_CODE[letter] ?? '';

const LESSON_LABEL: Record<LessonState, string> = {
  locked: 'op slot',
  letters: 'herkennen',
  words: 'woorden',
  done: 'klaar',
};

/** "2/3" for the word phase: the weakest of the lesson's letters. */
function wordProgress(lesson: number, progress: ProgressMap): string {
  const hits = Math.min(...MORSE_LESSONS[lesson]!.map((l) => progress[l]?.word_hits ?? 0));
  return `${Math.min(hits, WORD_HITS_NEEDED)}/${WORD_HITS_NEEDED}`;
}

/** Mastery as dots: filled up to the box, capped at the mastery mark. */
function BoxDots({ box }: { box: number }) {
  return (
    <span className="mo-dots" aria-label={`${Math.min(box, MASTERY_BOX)} van ${MASTERY_BOX}`}>
      {Array.from({ length: MASTERY_BOX }, (_, i) => (
        <span key={i} className={`mo-dot${i < box ? ' is-on' : ''}`} />
      ))}
    </span>
  );
}

/** The flashing part of the screen. Large when it is the only signal — then
 *  it is what the whole lesson is read from. */
function Lamp({ on, alone }: { on: boolean; alone: boolean }) {
  return <div className={`mo-lamp${on ? ' is-on' : ''}${alone ? ' is-alone' : ''}`} aria-hidden />;
}

/** "hoor", "zie" or "voel": how the code reaches the learner with these settings. */
function senseVerb(s: MorseSettings): { ask: string; show: string } {
  if (s.sound) return { ask: 'hoor', show: 'Laat horen' };
  if (s.flash) return { ask: 'zie', show: 'Laat zien' };
  if (s.vibrate) return { ask: 'voel', show: 'Laat voelen' };
  return { ask: 'hoor', show: 'Laat horen' };
}

type Feedback =
  | { kind: 'letter'; correct: boolean; expected: string; given: string }
  | { kind: 'word'; correct: boolean; expected: string; given: string };

export function MorseScreen() {
  const session = useSession();
  const userId = session.status === 'in' ? session.userId : null;
  const displayName = session.status === 'in' ? session.me?.user.display_name : undefined;
  const progress = useProgress(userId);
  const settings = useMorseSettings();

  const [practising, setPractising] = useState(false);
  const [step, setStep] = useState<MorseStep | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [typed, setTyped] = useState('');
  const [light, setLight] = useState(false);
  const [tally, setTally] = useState({ asked: 0, right: 0 });
  const [detail, setDetail] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [vibrateRefused, setVibrateRefused] = useState(false);

  const recent = useRef<string[]>([]);
  const playback = useRef<Playback | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A deploy must not reload the page mid-lesson.
  useHoldUpdates(practising);

  const stopSound = useCallback(() => {
    playback.current?.stop();
    playback.current = null;
  }, []);

  const playText = useCallback(
    (text: string) => {
      stopSound();
      unlockAudio();
      playback.current = play(text, {
        speed: { charWpm: settings.charWpm, effectiveWpm: settings.effectiveWpm },
        sound: settings.sound,
        flash: settings.flash,
        vibrate: settings.vibrate && canVibrate(),
        toneHz: settings.toneHz,
        onLight: setLight,
      });
      setVibrateRefused(playback.current.vibrated === false);
    },
    [settings, stopSound],
  );

  useEffect(
    () => () => {
      stopSound();
      if (timer.current) clearTimeout(timer.current);
    },
    [stopSound],
  );

  /** Ask the planner for the next step, given the map *after* the last write. */
  const advance = useCallback(
    (map: ProgressMap) => {
      if (timer.current) clearTimeout(timer.current);
      const s = nextStep({ progress: map, words: MORSE_WORDS, now: new Date(), recent: recent.current });
      const key = s.kind === 'word' ? s.word : s.letter;
      recent.current = [...recent.current, key].slice(-8);
      setStep(s);
      setFeedback(null);
      setTyped('');
      timer.current = setTimeout(() => playText(key), 450);
    },
    [playText],
  );

  const start = () => {
    unlockAudio();
    setPractising(true);
    setTally({ asked: 0, right: 0 });
    recent.current = [];
    if (progress) advance(progress);
  };

  const stop = () => {
    stopSound();
    if (timer.current) clearTimeout(timer.current);
    setPractising(false);
    setStep(null);
    setFeedback(null);
  };

  /* ---------------- answers ---------------- */

  const acceptIntro = async (letter: string, mnemonic: string) => {
    if (!progress || !userId) return;
    const p = { ...introduce(progressOf(progress, letter), new Date()), mnemonic: mnemonic.trim() || null };
    await saveLetter(userId, p);
    advance({ ...progress, [letter]: p });
  };

  const answerLetter = async (given: string) => {
    if (!progress || !userId || step?.kind !== 'letter' || feedback) return;
    const correct = given === step.letter;
    const p = recordAnswer(progressOf(progress, step.letter), correct, new Date());
    const next = { ...progress, [step.letter]: p };
    setTally((t) => ({ asked: t.asked + 1, right: t.right + (correct ? 1 : 0) }));
    setFeedback({ kind: 'letter', correct, expected: step.letter, given });
    await saveLetter(userId, p);
    if (correct) timer.current = setTimeout(() => advance(next), 700);
    // A miss: hear the right one again, then the learner moves on themselves.
    else timer.current = setTimeout(() => playText(step.letter), 500);
  };

  const answerWord = async () => {
    if (!progress || !userId || step?.kind !== 'word' || feedback) return;
    const given = typed.trim().toUpperCase();
    if (!given) return;
    const correct = given === step.word;
    const now = new Date();
    const changed: LetterProgress[] = [];
    if (correct) {
      // Each letter was recognised in context: a review answer and a word hit.
      for (const l of new Set(step.word)) {
        const p = recordAnswer(progressOf(progress, l), true, now);
        changed.push({ ...p, word_hits: p.word_hits + 1 });
      }
    } else {
      for (const l of wordMistakes(step.word, given)) changed.push(recordAnswer(progressOf(progress, l), false, now));
    }
    const next = { ...progress };
    for (const p of changed) next[p.letter] = p;
    setTally((t) => ({ asked: t.asked + 1, right: t.right + (correct ? 1 : 0) }));
    setFeedback({ kind: 'word', correct, expected: step.word, given });
    await saveLetters(userId, changed);
    if (correct) timer.current = setTimeout(() => advance(next), 900);
  };

  const proceed = () => progress && advance(progress);

  // A physical keyboard answers too: a letter key for a letter, Enter to go on.
  useEffect(() => {
    if (!practising || !progress) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (feedback && !feedback.correct && e.key === 'Enter') return proceed();
      if (step?.kind === 'letter' && !feedback && /^[a-z]$/i.test(e.key)) {
        const l = e.key.toUpperCase();
        if (progress[l]?.introduced_at) void answerLetter(l);
      }
      if (e.key === ' ' && step && step.kind !== 'intro') {
        e.preventDefault();
        playText(step.kind === 'word' ? step.word : step.letter);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  /* ---------------- render ---------------- */

  if (session.status === 'in' && !canSeeModule('morse', displayName)) return <Navigate to="/" replace />;

  const lesson = progress ? currentLesson(progress) : -1;
  const known = progress ? unlockedLetters(progress) : [];

  return (
    <div className="app">
      <div className="topbar">
        {practising ? (
          <button className="btn btn-quiet back" onClick={stop}>
            ‹ Stop
          </button>
        ) : (
          <Link className="btn btn-quiet back" to="/" aria-label="Naar het menu">
            ‹ Hub
          </Link>
        )}
        <h1>Morse</h1>
        <button className="btn btn-quiet" aria-label="Instellingen" onClick={() => setShowSettings(true)}>
          <SlidersIcon />
        </button>
      </div>

      {!progress ? null : practising && step ? (
        <main className="mo-main">
          <p className="mo-tally" aria-live="polite">
            Les {Math.max(lesson, 0) + 1} · {tally.right}/{tally.asked} goed
          </p>
          {settings.flash && <Lamp on={light} alone={!settings.sound} />}

          {step.kind === 'intro' && (
            <IntroCard
              key={step.letter}
              letter={step.letter}
              initialMnemonic={progress[step.letter]?.mnemonic ?? ''}
              playLabel={senseVerb(settings).show}
              onPlay={() => playText(step.letter)}
              onDone={(m) => void acceptIntro(step.letter, m)}
            />
          )}

          {step.kind === 'letter' && (
            <section className="mo-card">
              <h2 className="mo-prompt">Welke letter {senseVerb(settings).ask} je?</h2>
              <button className="btn mo-replay" onClick={() => playText(step.letter)}>
                <PlayIcon /> Nog eens
              </button>
              <div className="mo-answers" role="group" aria-label="Antwoorden">
                {[...known].sort().map((l) => {
                  const state =
                    feedback?.kind === 'letter'
                      ? l === feedback.expected
                        ? 'right'
                        : l === feedback.given
                          ? 'wrong'
                          : undefined
                      : undefined;
                  return (
                    <button
                      key={l}
                      className="mo-key"
                      data-state={state}
                      disabled={!!feedback}
                      onClick={() => void answerLetter(l)}
                    >
                      {l}
                    </button>
                  );
                })}
              </div>
              {feedback?.kind === 'letter' && (
                <LetterFeedback feedback={feedback} progress={progress} onNext={proceed} onPlay={playText} />
              )}
            </section>
          )}

          {step.kind === 'word' && (
            <section className="mo-card">
              <h2 className="mo-prompt">Typ het woord</h2>
              <button className="btn mo-replay" onClick={() => playText(step.word)}>
                <PlayIcon /> Nog eens
              </button>
              <form
                className="mo-word-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (feedback && !feedback.correct) proceed();
                  else void answerWord();
                }}
              >
                <input
                  className="input mo-word-input"
                  aria-label="Woord"
                  value={typed}
                  autoFocus
                  autoCapitalize="characters"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  readOnly={!!feedback}
                  onChange={(e) => setTyped(e.target.value.replace(/[^a-z]/gi, ''))}
                />
                {!feedback && (
                  <button className="btn btn-primary" type="submit" disabled={!typed.trim()}>
                    Check
                  </button>
                )}
              </form>
              {feedback?.kind === 'word' && (
                <WordFeedback feedback={feedback} progress={progress} onNext={proceed} onPlay={playText} />
              )}
            </section>
          )}
        </main>
      ) : (
        <Overview progress={progress} lesson={lesson} onStart={start} onOpen={setDetail} />
      )}

      {detail && progress && userId && (
        <LetterSheet
          letter={detail}
          progress={progressOf(progress, detail)}
          playLabel={senseVerb(settings).show}
          onPlay={() => playText(detail)}
          onSave={(m) => void saveLetter(userId, { ...progressOf(progress, detail), mnemonic: m.trim() || null })}
          onClose={() => setDetail(null)}
        />
      )}

      {showSettings && (
        <SettingsSheet
          settings={settings}
          vibrateRefused={vibrateRefused}
          onTry={() => playText('PARIS')}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}

/* ---------------- overview ---------------- */

function Overview({
  progress,
  lesson,
  onStart,
  onOpen,
}: {
  progress: ProgressMap;
  lesson: number;
  onStart: () => void;
  onOpen: (letter: string) => void;
}) {
  const known = unlockedLetters(progress);
  const state = lesson >= 0 ? lessonState(lesson, progress, MORSE_WORDS) : 'locked';
  const status =
    lesson < 0
      ? 'Je begint met E en T — de twee kortste tekens.'
      : state === 'letters'
        ? `Leer ${MORSE_LESSONS[lesson]!.join(' en ')} herkennen op gehoor.`
        : state === 'words'
          ? `Typ woorden met je letters — ${wordProgress(lesson, progress)}.`
          : lesson === MORSE_LESSONS.length - 1
            ? 'Alle letters geleerd. Oefenen houdt ze vers.'
            : `Les ${lesson + 1} is klaar — de volgende letters wachten.`;

  return (
    <main className="mo-main">
      <section className="mo-hero">
        <p className="mo-hero-k">{lesson < 0 ? 'Nog niet begonnen' : `Les ${lesson + 1} van ${MORSE_LESSONS.length}`}</p>
        <p className="mo-hero-status">{status}</p>
        <button className="btn btn-primary mo-start" onClick={onStart}>
          {lesson < 0 ? 'Begin' : 'Oefenen'}
        </button>
      </section>

      <ol className="mo-lessons" aria-label="Lessen">
        {MORSE_LESSONS.map((letters, i) => {
          const s = lessonState(i, progress, MORSE_WORDS);
          return (
            <li key={i} className="mo-lesson" data-state={s}>
              <span className="mo-lesson-n">{i + 1}</span>
              <span className="mo-lesson-letters">{letters.join(' ')}</span>
              <span className="mo-lesson-state">
                {s === 'words' ? `woorden ${wordProgress(i, progress)}` : LESSON_LABEL[s]}
              </span>
            </li>
          );
        })}
      </ol>

      {known.length > 0 && (
        <section>
          <h2 className="mo-h2">Je letters</h2>
          <div className="mo-letters">
            {known.map((l) => {
              const p = progress[l]!;
              return (
                <button key={l} className="mo-letter" onClick={() => onOpen(l)} aria-label={`Letter ${l}`}>
                  <span className="mo-letter-l">{l}</span>
                  <span className="mo-code">{glyphs(codeOf(l))}</span>
                  <span className={`mo-letter-m${p.mnemonic ? '' : ' is-empty'}`}>
                    {p.mnemonic || '+ ezelsbruggetje'}
                  </span>
                  <BoxDots box={p.box} />
                </button>
              );
            })}
          </div>
        </section>
      )}
    </main>
  );
}

/* ---------------- lesson cards ---------------- */

function IntroCard({
  letter,
  initialMnemonic,
  playLabel,
  onPlay,
  onDone,
}: {
  letter: string;
  initialMnemonic: string;
  playLabel: string;
  onPlay: () => void;
  onDone: (mnemonic: string) => void;
}) {
  const [mnemonic, setMnemonic] = useState(initialMnemonic);
  return (
    <section className="mo-card mo-intro">
      <p className="mo-new">Nieuwe letter</p>
      <div className="mo-big">{letter}</div>
      <div className="mo-code mo-code-big" aria-label={`code ${codeOf(letter).replace(/\./g, 'kort ').replace(/-/g, 'lang ')}`}>
        {glyphs(codeOf(letter))}
      </div>
      <button className="btn mo-replay" onClick={onPlay}>
        <PlayIcon /> {playLabel}
      </button>
      <label className="mo-field">
        <span>Jouw ezelsbruggetje</span>
        <input
          className="input"
          value={mnemonic}
          maxLength={300}
          placeholder="Bijv. een woord dat zo klinkt"
          onChange={(e) => setMnemonic(e.target.value)}
        />
      </label>
      <button className="btn btn-primary mo-start" onClick={() => onDone(mnemonic)}>
        Onthouden — test me
      </button>
    </section>
  );
}

function LetterFeedback({
  feedback,
  progress,
  onNext,
  onPlay,
}: {
  feedback: Feedback;
  progress: ProgressMap;
  onNext: () => void;
  onPlay: (t: string) => void;
}) {
  if (feedback.correct) {
    return (
      <p className="mo-verdict is-right" role="status">
        Goed! {feedback.expected} <span className="mo-code">{glyphs(codeOf(feedback.expected))}</span>
      </p>
    );
  }
  const mnemonic = progress[feedback.expected]?.mnemonic;
  return (
    <div className="mo-verdict is-wrong" role="status">
      <p>
        Het was <strong>{feedback.expected}</strong>{' '}
        <button className="mo-code mo-code-btn" onClick={() => onPlay(feedback.expected)}>
          {glyphs(codeOf(feedback.expected))}
        </button>
      </p>
      {mnemonic && <p className="mo-mnemonic">“{mnemonic}”</p>}
      <p className="mo-dim">
        Jij koos {feedback.given}{' '}
        <button className="mo-code mo-code-btn" onClick={() => onPlay(feedback.given)}>
          {glyphs(codeOf(feedback.given))}
        </button>
      </p>
      <button className="btn btn-primary mo-start" onClick={onNext} autoFocus>
        Verder
      </button>
    </div>
  );
}

function WordFeedback({
  feedback,
  progress,
  onNext,
  onPlay,
}: {
  feedback: Feedback;
  progress: ProgressMap;
  onNext: () => void;
  onPlay: (t: string) => void;
}) {
  if (feedback.correct) {
    return (
      <p className="mo-verdict is-right" role="status">
        Goed! {feedback.expected}
      </p>
    );
  }
  const given = feedback.given;
  return (
    <div className="mo-verdict is-wrong" role="status">
      <p>
        Het woord was <strong>{feedback.expected}</strong>
      </p>
      <ul className="mo-spell">
        {[...feedback.expected].map((l, i) => (
          <li key={i} data-state={given.length === feedback.expected.length && given[i] !== l ? 'wrong' : undefined}>
            <button className="mo-code-btn" onClick={() => onPlay(l)}>
              <strong>{l}</strong> <span className="mo-code">{glyphs(codeOf(l))}</span>
            </button>
            {progress[l]?.mnemonic && <span className="mo-mnemonic">{progress[l]!.mnemonic}</span>}
          </li>
        ))}
      </ul>
      <button className="btn btn-primary mo-start" onClick={onNext}>
        Verder
      </button>
    </div>
  );
}

/* ---------------- sheets ---------------- */

function LetterSheet({
  letter,
  progress,
  playLabel,
  onPlay,
  onSave,
  onClose,
}: {
  letter: string;
  progress: LetterProgress;
  playLabel: string;
  onPlay: () => void;
  onSave: (mnemonic: string) => void;
  onClose: () => void;
}) {
  const [mnemonic, setMnemonic] = useState(progress.mnemonic ?? '');
  const dirty = mnemonic.trim() !== (progress.mnemonic ?? '');
  const answered = progress.correct + progress.wrong;
  return (
    <Sheet title={`Letter ${letter}`} onClose={onClose}>
      <div className="mo-sheet">
        <div className="mo-big">{letter}</div>
        <div className="mo-code mo-code-big">{glyphs(codeOf(letter))}</div>
        <button className="btn mo-replay" onClick={onPlay}>
          <PlayIcon /> {playLabel}
        </button>
        <label className="mo-field">
          <span>Jouw ezelsbruggetje</span>
          <input className="input" value={mnemonic} maxLength={300} onChange={(e) => setMnemonic(e.target.value)} />
        </label>
        <button
          className="btn btn-primary"
          disabled={!dirty}
          onClick={() => {
            onSave(mnemonic);
            onClose();
          }}
        >
          Bewaar
        </button>
        <p className="mo-dim">
          {answered === 0
            ? 'Nog niet gevraagd.'
            : `${progress.correct} van ${answered} goed${progress.mastered_at ? ' · onthouden' : ''}.`}
        </p>
      </div>
    </Sheet>
  );
}

function SettingsSheet({
  settings,
  vibrateRefused,
  onTry,
  onClose,
}: {
  settings: MorseSettings;
  vibrateRefused: boolean;
  onTry: () => void;
  onClose: () => void;
}) {
  const set = (patch: Partial<MorseSettings>) => {
    const next = { ...settings, ...patch };
    // The overall pace can never outrun the letters themselves.
    next.effectiveWpm = Math.min(next.effectiveWpm, next.charWpm);
    void saveMorseSettings(next);
  };
  const vibrates = canVibrate();
  return (
    <Sheet title="Instellingen" onClose={onClose}>
      <div className="mo-settings">
        <div className="mo-recommend" role="group" aria-label="Aanbevolen instellingen">
          <span className="mo-recommend-k">Aanbevolen</span>
          <div className="mo-recommend-row">
            {(
              [
                ['sound', 'Met geluid', 'Leren op gehoor: alleen geluid, letters op 20 wpm, ruimte op 10.'],
                ['silent', 'Zonder geluid', 'Waar geluid niet kan: alleen flitsen, rustiger — letters op 12 wpm, ruimte op 6.'],
              ] as const
            ).map(([mode, label, hint]) => {
              const active = recommendedMode(settings) === mode;
              return (
                <button
                  key={mode}
                  className={`mo-preset${active ? ' is-active' : ''}`}
                  aria-pressed={active}
                  onClick={() => void saveMorseSettings(RECOMMENDED[mode])}
                >
                  <strong>
                    {active && '✓ '}
                    {label}
                  </strong>
                  <span>{hint}</span>
                </button>
              );
            })}
          </div>
        </div>

        <fieldset className="mo-modes">
          <legend>Hoe wil je het waarnemen?</legend>
          <label>
            <input type="checkbox" checked={settings.sound} onChange={(e) => set({ sound: e.target.checked })} /> Geluid
          </label>
          <label>
            <input type="checkbox" checked={settings.flash} onChange={(e) => set({ flash: e.target.checked })} /> Lichtflitsen
          </label>
          <label>
            <input
              type="checkbox"
              checked={settings.vibrate && vibrates}
              disabled={!vibrates}
              onChange={(e) => set({ vibrate: e.target.checked })}
            />{' '}
            Trillen{!vibrates && ' (niet op dit toestel)'}
          </label>
          {settings.vibrate && vibrates && (
            <p className="mo-dim">
              Met trillen speelt alles iets trager: korte tikjes voel je anders niet. Trilt er niets? Zet je
              telefoon niet op stil — Chrome trilt niet in de stille modus, en vertelt dat de app ook niet.
            </p>
          )}
          {settings.vibrate && vibrateRefused && (
            <p className="mo-warn" role="alert">
              Je browser weigerde het trillen. Tik nog eens op de knop hieronder.
            </p>
          )}
        </fieldset>

        <VibrationCheck />

        <label className="mo-range">
          <span>
            Lettersnelheid <strong>{settings.charWpm} wpm</strong>
          </span>
          <input
            type="range"
            min={12}
            max={30}
            value={settings.charWpm}
            onChange={(e) => set({ charWpm: Number(e.target.value) })}
          />
        </label>
        <label className="mo-range">
          <span>
            Tempo (ruimte tussen letters) <strong>{settings.effectiveWpm} wpm</strong>
          </span>
          <input
            type="range"
            min={4}
            max={settings.charWpm}
            value={settings.effectiveWpm}
            onChange={(e) => set({ effectiveWpm: Number(e.target.value) })}
          />
        </label>
        <label className="mo-range">
          <span>
            Toonhoogte <strong>{settings.toneHz} Hz</strong>
          </span>
          <input
            type="range"
            min={400}
            max={1000}
            step={10}
            value={settings.toneHz}
            onChange={(e) => set({ toneHz: Number(e.target.value) })}
          />
        </label>
        <p className="mo-dim">
          Letters klinken op volle snelheid, zodat je ze als één klank leert in plaats van puntjes te tellen; het
          tempo rekt alleen de stilte ertussen. Wordt het makkelijk, schuif dan het tempo omhoog.
        </p>
        <button className="btn" onClick={onTry}>
          <PlayIcon /> Probeer: PARIS
        </button>
      </div>
    </Sheet>
  );
}

/**
 * A plain one-second buzz with everything the browser will say about it — added
 * 2026-10-14 when the morse pattern did not vibrate on Jurren's phone (PWA,
 * vibrate mode) and nothing on the server side could explain it. It separates
 * the two questions: does this phone buzz for a web page at all (one plain
 * call, no sound playing), and if so, is it the pattern that fails.
 */
function VibrationCheck() {
  const [report, setReport] = useState<string | null>(null);
  if (!canVibrate()) return null;
  const run = (pattern: number | number[], label: string) => {
    const activation = (navigator as { userActivation?: { isActive: boolean; hasBeenActive: boolean } })
      .userActivation;
    const accepted = navigator.vibrate(pattern);
    setReport(
      `${label}: ${accepted ? 'browser accepteerde het' : 'browser weigerde het'}` +
        (activation ? ` · tik actief: ${activation.isActive ? 'ja' : 'nee'}` : '') +
        ` · ${document.visibilityState}`,
    );
  };
  return (
    <div className="mo-vcheck">
      <span>Trilt je telefoon?</span>
      <div className="mo-vcheck-row">
        <button className="btn" onClick={() => run(1000, '1 seconde')}>
          1 seconde
        </button>
        <button className="btn" onClick={() => run([300, 200, 300, 200, 300], 'Drie tikken')}>
          Drie tikken
        </button>
      </div>
      {report && (
        <p className="mo-dim" role="status">
          {report}
        </p>
      )}
    </div>
  );
}

/* ---------------- icons ---------------- */

function PlayIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M11 5 6 9H3v6h3l5 4V5Z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />
    </svg>
  );
}

function SlidersIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="10" cy="17" r="2" />
    </svg>
  );
}

